import type { Id } from '@odhaczka/core';
import { useEffect, useEffectEvent, useRef } from 'react';
import type { ListItemRow } from '../../db/types';
import { joinNames, plural, STATUS_LABEL } from './status';

export interface LiveNotice {
  text: string;
  /** Wzór wibracji (navigator.vibrate); brak = bez wibracji. */
  vibrate?: number[];
  /** Pozycja do pokazania po „Pokaż”. */
  itemId?: Id;
}

interface Options {
  listId: Id | undefined;
  me: string | null;
  nameOf: (userId: Id) => string;
  /** Od kiedy pozycje liczą się jako „dopisane w trakcie” (start mojej wizyty albo otwarcia listy). */
  since: number;
}

/** Cudze odhaczenia: najwyżej jeden komunikat na tyle ms, reszta zbiorczo. */
const GAP_MS = 3000;

type Snap = { status: ListItemRow['status']; suggestion: string | null };
type Checks = Map<Id, { count: number; last: ListItemRow }>;

/**
 * Wieści od domowników na otwartej liście: propozycja zamiennika, dopisana pozycja, cudze odhaczenia.
 * Porównuje kolejne wersje pozycji z Dexie (zmiany przychodzą synchronizacją); pierwsza wersja to tylko punkt odniesienia.
 */
export function useLiveNotices(items: ListItemRow[] | undefined, opts: Options, onNotice: (n: LiveNotice) => void): void {
  const prev = useRef<{ listId: Id | undefined; map: Map<Id, Snap> } | null>(null);
  const lastNoticeMs = useRef(0);
  const queued = useRef<Checks>(new Map());
  const timer = useRef<number | null>(null);
  const current = useEffectEvent(() => opts);
  const emit = useEffectEvent((n: LiveNotice) => {
    lastNoticeMs.current = Date.now();
    onNotice(n);
  });

  const flushChecks = useEffectEvent(() => {
    timer.current = null;
    const byUser = queued.current;
    if (byUser.size === 0) return;
    queued.current = new Map();
    const { nameOf } = current();
    const total = [...byUser.values()].reduce((n, c) => n + c.count, 0);
    const names = joinNames([...byUser.keys()].map(nameOf));
    const only = byUser.size === 1 ? [...byUser.values()][0] : undefined;
    emit({
      text:
        only && only.count === 1
          ? `${names}: ${only.last.name} → ${STATUS_LABEL[only.last.status]}`
          : `${names}: ${total} ${plural(total, 'pozycja odhaczona', 'pozycje odhaczone', 'pozycji odhaczonych')}`,
    });
  });

  const queueChecks = useEffectEvent((checks: ListItemRow[]) => {
    for (const i of checks) {
      const by = i.checkedBy!;
      const c = queued.current.get(by);
      queued.current.set(by, { count: (c?.count ?? 0) + 1, last: i });
    }
    if (timer.current !== null) return;
    const wait = lastNoticeMs.current + GAP_MS - Date.now();
    if (wait <= 0) flushChecks();
    else timer.current = window.setTimeout(flushChecks, wait);
  });

  useEffect(
    () => () => {
      if (timer.current !== null) window.clearTimeout(timer.current);
    },
    [],
  );

  useEffect(() => {
    if (!items) return;
    const { listId, me, nameOf, since } = current();
    const before = prev.current;
    prev.current = { listId, map: new Map(items.map((i) => [i.id, { status: i.status, suggestion: i.suggestion ?? null }])) };
    if (!before || before.listId !== listId) {
      queued.current = new Map();
      return;
    }
    const others = (by: Id | null | undefined): by is Id => !!by && by !== me;

    const checks = items.filter((i) => before.map.get(i.id)?.status === 'pending' && i.status !== 'pending' && others(i.checkedBy));
    if (checks.length > 0) queueChecks(checks);

    // Ważniejsze wieści na końcu — górny komunikat pokazuje ostatnią.
    const added = items.filter((i) => !before.map.has(i.id) && i.status === 'pending' && others(i.addedBy) && (i.addedMs ?? 0) > since);
    if (added.length > 0) {
      const people = [...new Set(added.map((i) => nameOf(i.addedBy!)))];
      const who = `${joinNames(people)} ${people.length > 1 ? 'dopisują' : 'dopisuje'}`;
      const shown = added.slice(0, 3).map((i) => i.name);
      const more = added.length > shown.length ? ` i ${added.length - shown.length} więcej` : '';
      emit({ text: `${who}: ${shown.join(', ')}${more}`, vibrate: [40], itemId: added[0]!.id });
    }

    const suggested = items.filter((i) => i.suggestion && others(i.suggestedBy) && before.map.get(i.id)?.suggestion !== i.suggestion);
    if (suggested.length > 0) {
      const people = [...new Set(suggested.map((i) => nameOf(i.suggestedBy!)))];
      const who = `${joinNames(people)} ${people.length > 1 ? 'proponują' : 'proponuje'}`;
      const first = suggested[0]!;
      emit({
        text:
          suggested.length === 1
            ? `${who} zamiennik: ${first.name} → ${first.suggestion}`
            : `${who} ${suggested.length} ${plural(suggested.length, 'zamiennik', 'zamienniki', 'zamienników')}`,
        vibrate: [30, 60, 30],
        itemId: first.id,
      });
    }
  }, [items]);
}
